import { useEffect, useState } from 'react';

import type { ReportKind, ReportNode, ReportResponse } from '@/api/client';
import { wikiApiClient } from '@/api/client';
import { useT } from '@/i18n/LocaleProvider';

export type ReportsProps = {
  lang?: string;
  onNavigate?: (slug: string) => void;
};

type TabEntry = {
  kind: ReportKind;
  labelKey: 'reports_wanted' | 'reports_orphans' | 'reports_dead_ends';
};

const tabs: TabEntry[] = [
  { kind: 'wanted', labelKey: 'reports_wanted' },
  { kind: 'orphans', labelKey: 'reports_orphans' },
  { kind: 'dead-ends', labelKey: 'reports_dead_ends' },
];

export function Reports({ lang, onNavigate }: ReportsProps) {
  const t = useT();
  const [activeKind, setActiveKind] = useState<ReportKind>('wanted');
  const [data, setData] = useState<ReportResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    wikiApiClient
      .getReport(activeKind, lang)
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(errorMessage(err, t('reports_error_load')));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeKind, t, lang]);

  const countLabel =
    data == null
      ? ''
      : data.total === 1
        ? t('reports_count_one')
        : t('reports_count_many', { count: data.total });

  return (
    <article className="note" data-testid="reports-view">
      <header className="note-header">
        <div className="note-kicker">
          <span>{countLabel}</span>
        </div>
        <h1>{t('reports_title')}</h1>
      </header>

      <div className="reports-tabs" role="tablist" aria-label={t('reports_title')}>
        {tabs.map((tab) => (
          <button
            key={tab.kind}
            role="tab"
            type="button"
            aria-selected={activeKind === tab.kind}
            className={activeKind === tab.kind ? 'is-active' : undefined}
            onClick={() => setActiveKind(tab.kind)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {isLoading ? <div className="empty-note">{t('reports_loading')}</div> : null}
      {!isLoading && error ? <div className="empty-note">{error}</div> : null}
      {!isLoading && !error && data?.nodes.length === 0 ? (
        <div className="empty-note">{t('reports_empty')}</div>
      ) : null}

      {!isLoading && !error && data && data.nodes.length > 0 ? (
        <ul className="page-index-list" data-testid="report-nodes">
          {data.nodes.map((node) => (
            <li key={node.id}>
              <ReportNodeItem node={node} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}

type ReportNodeItemProps = {
  node: ReportNode;
  onNavigate?: (slug: string) => void;
};

function ReportNodeItem({ node, onNavigate }: ReportNodeItemProps) {
  const slug = node.slug ?? node.id;

  if (onNavigate && slug) {
    return (
      <a
        href={`#${slug}`}
        onClick={(e) => {
          e.preventDefault();
          onNavigate(slug);
        }}
      >
        <strong>{node.title}</strong>
      </a>
    );
  }

  return <span>{node.title}</span>;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
